import { NumericOptionDropdown } from 'components/dropdown/NumericOptionDropdown';
import {
    getDisabledNumberOfSamplesOptions,
    getDisabledSamplingFrequencyOptions,
    mergeDisabledOptions,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/collectionDurationValidation';
import {
    getDisabledLfcNumberOfSamplesOptions,
    getDisabledLfcSamplingFrequencyOptions,
    lowFrequencyCollectionExceedsLimits,
} from 'components/node_settings/V4EditSettingsModal/CollectionSettingsTab/lowFrequencyCollectionValidation';
import { V4_SETTINGS_TEXT } from 'components/node_settings/v4SettingsTexts';
import { Setting } from 'components/node_settings/V4Settings/Setting';
import { SettingWarnings } from 'components/node_settings/V4Settings/SettingWarnings';
import { ToggleSetting } from 'components/node_settings/V4Settings/ToggleSetting';
import {
    DEFAULT_LFC_ENABLED,
    V4SensorSettings,
} from 'custom_hooks/data_hooks/v4_sensor_settings/shared';
import { useEffect } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { OptionChild } from 'utils/node';

const LFC_NUMBER_OF_SAMPLES_OPTIONS: OptionChild<number>[] = [
    { Value: 2000, Text: '2000 Samples' },
    { Value: 4000, Text: '4000 Samples' },
];

type LowFrequencyCollectionSettingProps = Readonly<{
    allAxesDisabled: boolean;
    samplingFrequencyOptions: OptionChild<number>[];
}>;

export function LowFrequencyCollectionSetting({
    allAxesDisabled,
    samplingFrequencyOptions,
}: LowFrequencyCollectionSettingProps) {
    const { control, setValue } = useFormContext<V4SensorSettings>();
    const [
        lowFrequencyCollection,
        primarySamplingFrequencyHz,
        primaryNumberOfSamples,
        updateInterval,
    ] = useWatch({
        control,
        name: [
            'lowFrequencyCollection',
            'samplingFrequencyHz',
            'numberOfSamples',
            'updateInterval',
        ],
    });

    const samplingFrequencyHz = lowFrequencyCollection.enabled
        ? lowFrequencyCollection.samplingFrequencyHz
        : undefined;
    const numberOfSamples = lowFrequencyCollection.enabled
        ? lowFrequencyCollection.numberOfSamples
        : undefined;

    const disabledSamplingFrequencyOptions = mergeDisabledOptions(
        getDisabledLfcSamplingFrequencyOptions(
            samplingFrequencyOptions,
            primarySamplingFrequencyHz,
        ),
        getDisabledSamplingFrequencyOptions(samplingFrequencyOptions, {
            numberOfSamples,
            updateInterval,
        }),
    );
    const disabledByPrimaryCap = getDisabledLfcNumberOfSamplesOptions(
        LFC_NUMBER_OF_SAMPLES_OPTIONS,
        primaryNumberOfSamples,
    );
    const disabledNumberOfSamplesOptions = mergeDisabledOptions(
        disabledByPrimaryCap,
        getDisabledNumberOfSamplesOptions(LFC_NUMBER_OF_SAMPLES_OPTIONS, {
            samplingFrequencyHz,
            updateInterval,
        }),
    );
    const noValidFrequency =
        disabledSamplingFrequencyOptions.length === samplingFrequencyOptions.length;
    const noValidNumberOfSamples =
        disabledNumberOfSamplesOptions.length === LFC_NUMBER_OF_SAMPLES_OPTIONS.length;

    const withinLimits = !lowFrequencyCollectionExceedsLimits({
        lowFrequencyCollection,
        primarySamplingFrequencyHz,
        primaryNumberOfSamples,
        updateInterval,
    });

    const primarySampleCapExhausted =
        !allAxesDisabled &&
        disabledByPrimaryCap.length === LFC_NUMBER_OF_SAMPLES_OPTIONS.length;

    useEffect(() => {
        if (
            (allAxesDisabled || primarySampleCapExhausted) &&
            lowFrequencyCollection.enabled
        ) {
            setValue('lowFrequencyCollection', { enabled: false });
        }
    }, [
        allAxesDisabled,
        primarySampleCapExhausted,
        lowFrequencyCollection.enabled,
        setValue,
    ]);

    const toggleDisabled = allAxesDisabled || primarySampleCapExhausted;

    const warnings: string[] = [];
    if (primarySampleCapExhausted) {
        warnings.push(V4_SETTINGS_TEXT.lowFrequencyCollection.primaryCapExhaustedWarning);
    }

    const helperText = (() => {
        if (allAxesDisabled) {
            return V4_SETTINGS_TEXT.disabledHints.allAxesDisabledHint;
        }
        if (withinLimits) {
            return V4_SETTINGS_TEXT.lowFrequencyCollection.enabledHelper;
        }
        if (noValidFrequency || noValidNumberOfSamples) {
            return V4_SETTINGS_TEXT.lowFrequencyCollection.noValidConfigHelper;
        }
        return V4_SETTINGS_TEXT.lowFrequencyCollection.exceedsLimitsHelper;
    })();

    return (
        <>
            {warnings.length > 0 && <SettingWarnings messages={warnings} />}
            <ToggleSetting
                name={V4_SETTINGS_TEXT.settingNames.lowFrequencyCollection}
                ariaLabel={V4_SETTINGS_TEXT.settingNames.lowFrequencyCollection}
                helperText={helperText}
                checked={!toggleDisabled && lowFrequencyCollection.enabled}
                disabled={toggleDisabled}
                onChange={(checked) => {
                    if (!checked) {
                        setValue(
                            'lowFrequencyCollection',
                            { enabled: false },
                            { shouldDirty: true, shouldValidate: true },
                        );
                        return;
                    }
                    const validFrequencyOptions = samplingFrequencyOptions.filter(
                        (option) => option.Value < primarySamplingFrequencyHz,
                    );
                    const defaultSamplingFrequencyHz = validFrequencyOptions.some(
                        (option) =>
                            option.Value === DEFAULT_LFC_ENABLED.samplingFrequencyHz,
                    )
                        ? DEFAULT_LFC_ENABLED.samplingFrequencyHz
                        : (validFrequencyOptions.at(-1)?.Value ??
                          DEFAULT_LFC_ENABLED.samplingFrequencyHz);
                    setValue(
                        'lowFrequencyCollection',
                        {
                            ...DEFAULT_LFC_ENABLED,
                            samplingFrequencyHz: defaultSamplingFrequencyHz,
                        },
                        { shouldDirty: true, shouldValidate: true },
                    );
                }}
            />

            {!toggleDisabled && lowFrequencyCollection.enabled && (
                <>
                    <Setting
                        name={V4_SETTINGS_TEXT.settingNames.samplingFrequency}
                        indented
                    >
                        <Controller
                            control={control}
                            name="lowFrequencyCollection.samplingFrequencyHz"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={samplingFrequencyOptions}
                                    onChange={field.onChange}
                                    disabledOptions={disabledSamplingFrequencyOptions}
                                />
                            )}
                        />
                    </Setting>
                    <Setting
                        name={V4_SETTINGS_TEXT.settingNames.numberOfSamples}
                        indented
                    >
                        <Controller
                            control={control}
                            name="lowFrequencyCollection.numberOfSamples"
                            render={({ field }) => (
                                <NumericOptionDropdown
                                    value={field.value}
                                    options={LFC_NUMBER_OF_SAMPLES_OPTIONS}
                                    onChange={field.onChange}
                                    width="14rem"
                                    disabledOptions={disabledNumberOfSamplesOptions}
                                />
                            )}
                        />
                    </Setting>
                </>
            )}
        </>
    );
}
